import { useState } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { toast } from '@/components/ui/Toast';
import {
  useBanks,
  useCreateBank,
  useUpdateBank,
  useDeleteBank,
  type PaymentBank,
  type PaymentBankInput,
} from './api';

const EMPTY: PaymentBankInput = {
  bankCode: '',
  bankName: '',
  accountName: '',
  accountNumber: '',
  sortOrder: 0,
  active: true,
};

export function BanksPage() {
  const { data: banks, isLoading, isError, refetch } = useBanks();
  const [editing, setEditing] = useState<PaymentBank | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PaymentBank | null>(null);

  return (
    <Card padding="none">
      <div className="p-5">
        <CardHeader
          title="Payment banks"
          description="Bank accounts shown on the customer app's bank-transfer screen."
          actions={
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add bank
            </Button>
          }
        />
      </div>

      {isLoading && (
        <div className="space-y-2 px-5 pb-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {isError && (
        <div className="px-5 pb-8">
          <ErrorState
            title="Could not load banks"
            onRetry={() => refetch()}
          />
        </div>
      )}

      {!isLoading && !isError && banks && banks.length === 0 && (
        <div className="px-5 pb-8">
          <EmptyState
            title="No banks yet"
            description="Add the bank accounts customers can transfer to."
          />
        </div>
      )}

      {!isLoading && !isError && banks && banks.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-y border-line text-caption text-ink-muted">
                <th className="px-5 py-3 font-medium">Bank</th>
                <th className="px-5 py-3 font-medium">Account name</th>
                <th className="px-5 py-3 font-medium">Account number</th>
                <th className="px-5 py-3 font-medium">Order</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {banks.map((b) => (
                <tr key={b.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3 text-body-strong text-ink">{b.bankName}</td>
                  <td className="px-5 py-3 text-body text-ink">{b.accountName}</td>
                  <td className="px-5 py-3 text-body text-ink font-mono">{b.accountNumber}</td>
                  <td className="px-5 py-3 text-body text-ink-muted">{b.sortOrder}</td>
                  <td className="px-5 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-pill px-2.5 py-0.5 text-overline uppercase whitespace-nowrap',
                        b.active
                          ? 'bg-status-success-bg text-status-success'
                          : 'bg-line-soft text-ink-muted',
                      )}
                    >
                      {b.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(b)}
                        aria-label={`Edit ${b.bankName}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleting(b)}
                        aria-label={`Delete ${b.bankName}`}
                      >
                        <Trash2 className="h-4 w-4 text-status-error" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <BankFormModal
          title="Add bank"
          initial={EMPTY}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <BankFormModal
          title="Edit bank"
          initial={editing}
          bankId={editing.id}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteBankModal bank={deleting} onClose={() => setDeleting(null)} />
      )}
    </Card>
  );
}

function BankFormModal({
  title,
  initial,
  bankId,
  onClose,
}: {
  title: string;
  initial: PaymentBankInput;
  bankId?: number;
  onClose: () => void;
}) {
  const [form, setForm] = useState<PaymentBankInput>({
    bankCode: initial.bankCode,
    bankName: initial.bankName,
    accountName: initial.accountName,
    accountNumber: initial.accountNumber,
    sortOrder: initial.sortOrder ?? 0,
    active: initial.active ?? true,
  });
  const create = useCreateBank();
  const update = useUpdateBank();
  const saving = create.isPending || update.isPending;

  const set = (patch: Partial<PaymentBankInput>) =>
    setForm((f) => ({ ...f, ...patch }));

  const valid =
    form.bankCode.trim() &&
    form.bankName.trim() &&
    form.accountName.trim() &&
    form.accountNumber.trim();

  async function save() {
    if (!valid) return;
    const payload: PaymentBankInput = {
      ...form,
      bankCode: form.bankCode.trim(),
      bankName: form.bankName.trim(),
      accountName: form.accountName.trim(),
      accountNumber: form.accountNumber.trim(),
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (bankId != null) {
        await update.mutateAsync({ id: bankId, input: payload });
        toast.success('Bank updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Bank created');
      }
      onClose();
    } catch (e) {
      toast.error('Could not save bank', errorMessage(e));
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} disabled={!valid}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Bank code"
          placeholder="EQUITY"
          value={form.bankCode}
          onChange={(e) => set({ bankCode: e.target.value })}
        />
        <Input
          label="Bank name"
          placeholder="Equity"
          value={form.bankName}
          onChange={(e) => set({ bankName: e.target.value })}
        />
        <Input
          label="Account name"
          placeholder="KISUMU WATER"
          value={form.accountName}
          onChange={(e) => set({ accountName: e.target.value })}
        />
        <Input
          label="Account number"
          placeholder="0290265172985"
          value={form.accountNumber}
          onChange={(e) => set({ accountNumber: e.target.value })}
        />
        <Input
          label="Display order"
          type="number"
          value={String(form.sortOrder ?? 0)}
          onChange={(e) => set({ sortOrder: Number(e.target.value) })}
        />
        <Checkbox
          label="Active (shown in the app)"
          checked={form.active ?? true}
          onChange={(e) => set({ active: e.target.checked })}
        />
      </div>
    </Modal>
  );
}

function DeleteBankModal({
  bank,
  onClose,
}: {
  bank: PaymentBank;
  onClose: () => void;
}) {
  const remove = useDeleteBank();
  async function confirm() {
    try {
      await remove.mutateAsync(bank.id);
      toast.success('Bank deleted');
      onClose();
    } catch (e) {
      toast.error('Could not delete bank', errorMessage(e));
    }
  }
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Delete bank"
      description={`Remove ${bank.bankName} (${bank.accountNumber})? Customers will no longer see this account.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm} loading={remove.isPending}>
            Delete
          </Button>
        </>
      }
    />
  );
}

function errorMessage(e: unknown): string | undefined {
  const resp = (e as { response?: { data?: { message?: string } } })?.response;
  return resp?.data?.message;
}
